import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TherapistRegistration.css';
import {
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { auth, db } from './config/firebaseConfig';
import Navbar from './Navbar';
import Footer from './Footer';
import copyMultipleCollections from './copyMultipleCollections';

// Standardized Password Validation (matching mobile app)
const ALLOWED_SPECIAL_CHARS = '@$!%*?&#+=~`|<>_-.,;:()[]{}^';

// Improved repeated character validation
const hasExcessiveRepeats = (str) => {
  if (!str || typeof str !== 'string') return false;
  
  // Block 4+ consecutive identical characters
  if (/(.)\1{3,}/.test(str)) {
    return true;
  }
  
  // Block obvious patterns like all same character
  if (/^(.)\1+$/.test(str)) {
    return true;
  }
  
  return false;
};

// Comprehensive username validation (matching mobile app)
const validateUsername = (username) => {
  if (!username || typeof username !== 'string') {
    return "Username is required";
  }
  
  const trimmedUsername = username.trim();
  
  // Length validation
  if (trimmedUsername.length < 4) {
    return "Username must be at least 4 characters";
  }
  
  if (trimmedUsername.length > 20) {
    return "Username must be no more than 20 characters";
  }
  
  // More flexible character validation (matching mobile app)
  if (!/^[a-zA-Z0-9._-]+$/.test(trimmedUsername)) {
    return "Username can only contain letters, numbers, dots, underscores, and hyphens";
  }
  
  // Cannot start or end with special characters
  if (/^[._-]|[._-]$/.test(trimmedUsername)) {
    return "Username cannot start or end with special characters";
  }
  
  // Cannot have consecutive special characters
  if (/[._-]{2,}/.test(trimmedUsername)) {
    return "Username cannot have consecutive special characters";
  }
  
  // Check for excessive repeated characters
  if (hasExcessiveRepeats(trimmedUsername)) {
    return "Username cannot have excessive repeated characters";
  }
  
  // Enhanced reserved usernames list
  const reservedUsernames = [
    'admin', 'administrator', 'root', 'system', 'test', 'user', 'guest',
    'support', 'help', 'api', 'null', 'undefined', 'anonymous', 'bot',
    'moderator', 'mod', 'owner', 'superuser', 'service', 'daemon',
    'www', 'mail', 'ftp', 'localhost', 'webmaster', 'postmaster'
  ];
  
  if (reservedUsernames.includes(trimmedUsername.toLowerCase())) {
    return "This username is not available";
  }
  
  // Check for inappropriate content
  if (/fuck|shit|damn|hell|bitch|ass/i.test(trimmedUsername)) {
    return "Username contains inappropriate content";
  }
  
  return null; // Valid username
};

// Enhanced password validation function (matching mobile app)
const validatePassword = (password, username = '') => {
  if (!password || typeof password !== 'string') {
    return "Password is required";
  }
  
  // Length validation
  if (password.length < 8) {
    return "Password must be at least 8 characters";
  }
  
  if (password.length > 128) {
    return "Password is too long";
  }
  
  // Character type requirements
  if (!/[a-z]/.test(password)) {
    return "Password must contain at least one lowercase letter";
  }
  
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter";
  }
  
  if (!/\d/.test(password)) {
    return "Password must contain at least one number";
  }
  
  // Updated special character validation (matches mobile app)
  if (!/[@$!%*?&#+=~`|<>_\-.,;:()\[\]{}^]/.test(password)) {
    return `Password must contain at least one special character (${ALLOWED_SPECIAL_CHARS})`;
  }
  
  // Enhanced weak passwords list
  const weakPasswords = [
    'password', '12345678', 'qwerty123', 'password123', 'admin123',
    'welcome123', 'letmein123', 'monkey123', '123456789', 'password1',
    'iloveyou', 'princess', 'rockyou', 'abc123', 'nicole', 'daniel',
    'babygirl', 'monkey', 'lovely', 'jessica'
  ];
  
  if (weakPasswords.includes(password.toLowerCase())) {
    return "This password is too common. Please choose a stronger one";
  }
  
  // Cannot contain username
  if (username && password.toLowerCase().includes(username.toLowerCase())) {
    return "Password cannot contain your username";
  }
  
  // Check for excessive repeated characters
  if (hasExcessiveRepeats(password)) {
    return "Password cannot have excessive repeated characters";
  }
  
  // Check for keyboard patterns
  const keyboardPatterns = [
    'qwerty', 'asdf', 'zxcv', '1234', 'abcd', 'qwer', 'asdfg'
  ];
  
  for (const pattern of keyboardPatterns) {
    if (password.toLowerCase().includes(pattern)) {
      return "Password cannot contain common keyboard patterns";
    }
  }
  
  return null; // Valid password
};

// Updated regex to match mobile app
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#+=~`|<>_\-.,;:()\[\]{}^])[A-Za-z\d@$!%*?&#+=~`|<>_\-.,;:()\[\]{}^]{8,}$/;

const ClientRegistration = () => {
  const [alias, setAlias] = useState('');
  const [password, setPassword] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error1, setError1] = useState(null);
  const [success1, setSuccess1] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => setIsPasswordVisible(prev => !prev);
  const navigate = useNavigate();

  // Username validation
  const handleAliasChange = e => {
    const value = e.target.value;
    setAlias(value);

    const validationError = validateUsername(value);
    if (validationError) {
      setAliasError(validationError);
    } else {
      setAliasError('');
    }
  };

  // Password validation
  const handlePasswordChange = e => {
    const value = e.target.value;
    setPassword(value);

    const validationError = validatePassword(value, alias);
    if (validationError) {
      setPasswordError(validationError);
    } else {
      setPasswordError('');
    }
  };

  const logEvent = async (alias, role, eventType, note) => {
    try {
      await addDoc(collection(db, 'Logs'), {
        timestamp: serverTimestamp(),
        eventType,
        alias,
        role,
        note,
      });
      console.log('Event logged successfully');
    } catch (error) {
      console.error('Error logging event:', error);
    }
  };

  // Generate a unique system email for the user
  const generateSystemEmail = (alias) => {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 10);
    return `${alias}-${timestamp}-${randomString}@ocdserenity-system.com`;
  };

  const handleRegister = async e => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Check if alias already exists in Firestore
      const aliasQuery = query(
        collection(db, 'users'),
        where('alias', '==', alias)
      );
      const aliasSnapshot = await getDocs(aliasQuery);

      if (!aliasSnapshot.empty) {
        setError('This alias is already taken. Please choose a different alias.');
        setLoading(false);
        return;
      }

      // Ask user if they want to register with therapist key or individually
      setModalOpen(true);
    } catch (err) {
      console.error('Error checking alias existence:', err);
      setError('An error occurred while verifying the alias. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSecretKeySubmit = async () => {
    setError1(null);

    if (secretKey.trim()) {
      try {
        const therapistsQuery = query(
          collection(db, 'users'),
          where('therapistSecretKey', '==', secretKey)
        );
        const querySnapshot = await getDocs(therapistsQuery);

        if (!querySnapshot.empty) {
          const therapistDoc = querySnapshot.docs[0];
          const therapistId = therapistDoc.id;

          // Generate a system email for Firebase Auth
          const systemEmail = generateSystemEmail(alias);
          
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            systemEmail,
            password
          );
          const user = userCredential.user;

          await finalizeRegistrationClient(user, therapistId, systemEmail);
          navigate('/client-dashboard');
        } else {
          setError1('Invalid Therapist Secret Key. Please try again.');
          await logEvent(
            alias,
            'client',
            'registration',
            'Invalid Therapist Secret Key.'
          );
        }
      } catch (error) {
        console.error('Error verifying secret key:', error);
        setError1('Failed to verify Therapist Secret Key. Please try again.');
      }
    } else {
      setError1('Please enter a valid Therapist Secret Key.');
    }
  };

  const handleSkip = async () => {
    setModalOpen(false);
    registerAsIndividual();
  };

  const registerAsIndividual = async () => {
    setError(null);
    setLoading(true);
    try {
      // Generate a system email for Firebase Auth
      const systemEmail = generateSystemEmail(alias);
      
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        systemEmail,
        password
      );
      const user = userCredential.user;

      await finalizeRegistration(user, systemEmail);
      navigate('/client-dashboard');
    } catch (error) {
      console.error('Error registering individual:', error);
      setError('Failed to register. Please try again.');
      await logEvent(
        alias,
        'individual',
        'registration',
        'Registration failed.'
      );
    } finally {
      setLoading(false);
    }
  };

  const finalizeRegistrationClient = async (user, therapistId, systemEmail) => {
    try {
      await setDoc(doc(db, 'users', user.uid), {
        alias,
        systemEmail,
        role: 'client',
        therapistId,
        comment: '',
        status: 'sponsored',
        createdAt: serverTimestamp(),
        recoveryEnabled: false,
        passwordRecoveryDisabled: true,
        accountCreatedWithoutRecovery: true,
      });

      /*
      const TrackerSourceUID = 'ADjKRnX1iXduMuFV3GRmpjnAk7m1';
      await copyMultipleCollections(TrackerSourceUID, user.uid, [
        'moodTracker',
        'ocdSuds',
        'ocdTriggers',
        'exposureHierarchy',
        'couponHistory',
      ]);
      */

      await logEvent(
        alias,
        'client',
        'registration',
        'Client successfully registered and linked to therapist.'
      );

      setSuccess1('Registration complete!');
      setAlias('');
      setPassword('');
      setModalOpen(false);
    } catch (error) {
      console.error('Error finalizing registration:', error);
      setError1(
        'An error occurred during registration. Please contact support.'
      );
      await logEvent(
        alias,
        'client',
        'registration',
        'An error occurred during registration.'
      );
    }
  };

  const finalizeRegistration = async (user, systemEmail) => {
    try {
      await setDoc(doc(db, 'users', user.uid), {
        alias,
        systemEmail,
        role: 'client',
        therapistId: null,
        comment: "Provide a description of the patient's background, condition, and any notable observations.",
        status: 'sponsored',
        createdAt: serverTimestamp(),
        recoveryEnabled: false,
        passwordRecoveryDisabled: true,
        accountCreatedWithoutRecovery: true,
      });

      /*
      const TrackerSourceUID = 'ADjKRnX1iXduMuFV3GRmpjnAk7m1';
      await copyMultipleCollections(TrackerSourceUID, user.uid, [
        'moodTracker',
        'ocdSuds',
        'ocdTriggers',
        'exposureHierarchy',
        'couponHistory',
        'journals',
        'ocdGoals',
      ]);
      */

      await logEvent(
        alias,
        'individual',
        'registration',
        'Individual registration complete!'
      );

      setSuccess('Registration complete!');
      setAlias('');
      setPassword('');
      setModalOpen(false);
    } catch (error) {
      console.error('Error finalizing registration:', error);
      setError(
        'An error occurred during registration. Please contact support.'
      );
      await logEvent(
        alias,
        'individual',
        'registration',
        'An error occurred during registration.'
      );
    }
  };

  return (
    <>
      <Navbar />
      <div className='therapist-registration'>
        <h2>Anonymous Registration</h2>
        
        

        <form onSubmit={handleRegister} className='registration-form'>
          <label>
            Username:
            <input
              type='text'
              value={alias}
              onChange={handleAliasChange}
              required
              placeholder="Choose a unique username (no email needed)"
            />
            {aliasError && <p className='error'>{aliasError}</p>}
          </label>
          
          <label>
            Password:
            <div style={{ position: 'relative' }}>
              <input
                type={isPasswordVisible ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                required
                placeholder="Create a strong password you can remember"
              />
              <span
                onClick={togglePasswordVisibility}
                style={{
                  position: 'absolute',
                  right: '25px',
                  top: '50%',
                  fontSize: '25px',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                }}
              >
                {isPasswordVisible ? '👁️' : '🙈'}
              </span>
            </div>
            {passwordError && <p className='error'>{passwordError}</p>}
            
            {/* Password Requirements Helper */}
            {password.length > 0 && !PASSWORD_REGEX.test(password) && (
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #dee2e6',
                borderRadius: '8px', 
                padding: '12px', 
                marginTop: '8px',
                fontSize: '12px',
                color: '#6c757d'
              }}>
                <strong style={{ color: '#495057' }}>Password Requirements:</strong><br/>
                • At least 8 characters<br/>
                • Uppercase and lowercase letters<br/>
                • At least one number<br/>
                • Special character: {ALLOWED_SPECIAL_CHARS}<br/>
                • No keyboard patterns (qwerty, 1234, etc.)<br/>
                • No excessive repeated characters
              </div>
            )}

            {/* Password Tips */}
            <div style={{ 
              backgroundColor: '#e7f3ff', 
              border: '1px solid #b8daff',
              borderRadius: '8px', 
              padding: '12px', 
              marginTop: '12px',
              fontSize: '12px',
              color: '#004085'
            }}>
              <strong>💡 Password Tips:</strong><br/>
              • Use a phrase you can remember: "ILove2Hike!2024"<br/>
              • Combine words, numbers, and symbols<br/>
              • Avoid personal information (birthdays, names)<br/>
              • Make it unique to this app<br/>
              <br/>
              <span style={{ fontWeight: 'bold', color: '#dc3545' }}>
                ⚠️ Your password cannot be recovered if forgotten
              </span>
            </div>
          </label>

          {loading ? (
            <p>Registering...</p>
          ) : (
            <button
              type='submit'
              disabled={!!aliasError || !!passwordError || !alias || !password}
            >
              Create Account
            </button>
          )}
          
          {error && <p className='error'>{error}</p>}
          {success && <p className='success'>{success}</p>}
        </form>

        {/* Therapist Secret Key Modal */}
        {modalOpen && (
          <div className='r-modal-overlay'>
            <div className='r-modal'>
              <h3>Registration Options</h3>
              <p style={{ textAlign: 'justify', marginBottom: '16px' }}>
                Would you like to register with a <strong>therapist's secret key</strong> or as an <strong>individual</strong>?
              </p>
              <p style={{ textAlign: 'justify', marginBottom: '20px' }}>
                The therapist's secret key is an 8-digit code provided by your therapist. 
                This key allows your therapist to access your dashboard and support you on your journey to recovery.
              </p>
              
              <div style={{ marginBottom: '16px' }}>
                <input
                  type='text'
                  value={secretKey}
                  onChange={e => setSecretKey(e.target.value.toUpperCase())}
                  placeholder='Enter Therapist Secret Key (optional)'
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    marginBottom: '12px',
                    borderRadius: '8px',
                    border: '1px solid #ccc'
                  }}
                />
                <button 
                  onClick={handleSecretKeySubmit}
                  disabled={!secretKey.trim()}
                  style={{ 
                    width: '100%', 
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: secretKey.trim() ? '#4caf50' : '#ccc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: secretKey.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  Register with Therapist Key
                </button>
              </div>

              <button 
                onClick={handleSkip}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Register as Individual (No Therapist)
              </button>

              {error1 && <p className='error' style={{ marginTop: '12px' }}>{error1}</p>}
              {success1 && <p className='success' style={{ marginTop: '12px' }}>{success1}</p>}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ClientRegistration;